import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import { ConfiguredProductListItem } from '../list/ListTypes'
import { AppReducerAction, AppState, AppStateActions } from './AppState'
import { logDebug } from '../debug/debugLogging'
import { ProductSeries } from '../types/productTypes'
import { UserType } from '../types/userTypes'

type AppStateReducerType = React.Reducer<Readonly<AppState>, Readonly<AppReducerAction>>

function mapProductsWithUpdate(
  currentItems: readonly ConfiguredProductListItem[],
  idToUpdate: string,
  cb: (listProduct: ConfiguredProductListItem) => ConfiguredProductListItem
): readonly ConfiguredProductListItem[] {
  return currentItems.map(it => it.configuredProduct.uid === idToUpdate ? cb(it) : it)
}

const AppStateActionHandlerReducer: AppStateReducerType = (currentState, action): Readonly<AppState> => {
  const { userState: currentUserState } = currentState
  switch (action.action) {
    case AppStateActions.ToggleIsAuthenticated:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          isAuthenticated: !currentUserState.isAuthenticated
        }
      }
    case AppStateActions.ToggleMoreInfo:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          moreInfo: !currentUserState.moreInfo
        }
      }
    case AppStateActions.SaveProduct:
      let didUpdate = false
      // let focusedListItemUid: string | null = null

      let products: readonly ConfiguredProductListItem[] = mapProductsWithUpdate(
        currentUserState.configuredProducts,
        action.product.uid, (it) => {
          didUpdate = true
          return ({ ...it, configuredProduct: action.product })
        }
      )

      // add new product if it doesn't exist
      // focusedListItemUid = action.product.uid
      if (!didUpdate) {
        // focusedListItemUid = action.product.uid
        const newProduct: ConfiguredProductListItem = {
          quantity: 1,
          note: '',
          roomId: null,
          configuredProduct: action.product
        }

        products = [...currentUserState.configuredProducts, newProduct]
      }

      return {
        ...currentState,
        userState: {
          ...currentUserState,
          configuredProducts: products,
        },
        // screen: 'list',
        isEditing: false,
        editingProductId: action.product.uid,
        focusedListItemUid: action.product.uid,
      }
    case AppStateActions.NewProduct:
      return {
        ...currentState,
        screen: null,
        isEditing: false,
        focusedListItemUid: null,
        editingProductId: uuidv4(),
      }
    case AppStateActions.ToggleListDisplayed:
      if (currentState.screen === 'list') {
        return {
          ...currentState,
          focusedListItemUid: null,
          screen: null,
        }
      }

      return {
        ...currentState,
        screen: 'list',
      }
    case AppStateActions.SetListDisplayed:
      return {
        ...currentState,
        focusedListItemUid: null,
        screen: action.displayed ? 'list' : null,
      }
    case AppStateActions.EditProduct:
      return {
        ...currentState,
        screen: 'list',
        isEditing: true,
        editingProductId: action.uid,
        focusedListItemUid: null,
      }
    case AppStateActions.DuplicateProduct:
      const currentProduct = currentUserState.configuredProducts.find(it => it.configuredProduct.uid === action.uid)

      if (!currentProduct) {
        return currentState
      }

      const uid = uuidv4()

      return {
        ...currentState,
        focusedListItemUid: uid,
        userState: {
          ...currentUserState,
          configuredProducts: [
            ...currentUserState.configuredProducts,
            {
              ...currentProduct,
              quantity: 1,
              configuredProduct: {
                ...currentProduct.configuredProduct,
                uid,
              },
            },
          ],
        },
      }
    case AppStateActions.CancelEditing:
      return {
        ...currentState,
        screen: 'list',
        isEditing: false,
      }
    case AppStateActions.ChangeQuantity:
      if (currentState.screen === 'list') {
        const { newQuantity, uid } = action
        if (newQuantity < 1) {
          return {
            ...currentState,
            userState: {
              ...currentUserState,
              configuredProducts: currentUserState.configuredProducts.filter(it => it.configuredProduct.uid !== uid)
            }
          }
        }

        return {
          ...currentState,
          userState: {
            ...currentUserState,
            configuredProducts: mapProductsWithUpdate(currentUserState.configuredProducts, uid, (it) => ({
              ...it,
              quantity: newQuantity,
            })),
          },
        }
      }

      return currentState
    case AppStateActions.ChangeNote:
      const { newNote } = action
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          configuredProducts: mapProductsWithUpdate(currentUserState.configuredProducts, action.listItemUid, (it) => ({
            ...it,
            note: newNote
          }))
        },
      }
    case AppStateActions.ChangeRoom:
      const { roomId } = action

      const updatedListItems = mapProductsWithUpdate(currentUserState.configuredProducts, action.listItemUid, (it) => ({
        ...it,
        roomId,
      }))

      // sort updated item to end
      const sortedItems = updatedListItems
        .map((item, index, array) => ({
          item,
          index: item.configuredProduct.uid === action.listItemUid ? (array.length * 2) : index
        }))
        .sort((a, b) => a.index - b.index)
        .map(it => it.item)

      return {
        ...currentState,
        userState: {
          ...currentUserState,
          configuredProducts: sortedItems,
        },
        focusedListItemUid: action.listItemUid,
      }
    case AppStateActions.AddRoom:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          rooms: [...currentUserState.rooms, {
            name: action.name,
            uid: uuidv4(),
            expanded: true,
          }],
        }
      }
    case AppStateActions.DeleteRoom:
      const configuredProducts = action.keepItems
        // blank out room
        ? currentUserState.configuredProducts.map(it => it.roomId === action.uid ? ({ ...it, roomId: null }) : it)
        // or delete items in room
        : currentUserState.configuredProducts.filter(it => it.roomId !== action.uid)

      return {
        ...currentState,
        userState: {
          ...currentUserState,
          configuredProducts,
          rooms: currentUserState.rooms.filter(it => it.uid !== action.uid),
        },
        focusedListItemUid: null,
      }
    case AppStateActions.RenameRoom:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          rooms: currentUserState.rooms.map(it => it.uid === action.uid ? { ...it, name: action.name } : it),
        }
      }
    case AppStateActions.OpenListFromBanner:
      return {
        ...currentState,
        screen: 'list',
        focusedListItemUid: action.uid,
      }
    case AppStateActions.SelectUserType:
      if (currentState.userState.userType === action.userType) {
        return currentState
      }

      return {
        ...currentState,
        userState: {
          ...currentUserState,
          userType: action.userType,
          selectedProductSeries: null,
        },
      }
    case AppStateActions.RenameList:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          listName: action.newName.trim(),
        }
      }
    case AppStateActions.ClearList:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          listName: action.newListName,
          listId: action.newListId,
          rooms: [],
          configuredProducts: [],
        },
        focusedListItemUid: null,
      }
    case AppStateActions.OpenSharingList:
      return {
        ...currentState,
        screen: currentState.screen === 'sharingList' ? null : 'sharingList',
      }
    case AppStateActions.OpenSharingBom:
      return {
        ...currentState,
        screen: currentState.screen === 'sharingBom' ? null : 'sharingBom',
      }
    case AppStateActions.OpenWallColor:
      return {
        ...currentState,
        screen: currentState.screen === 'wallColor' ? null : 'wallColor',
      }
    case AppStateActions.ChangeWallColor:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          wallColor: action.color,
        },
      }
    case AppStateActions.SetDebugEnabled:
      return {
        ...currentState,
        debugMode: action.enabled,
      }
    case AppStateActions.RoomExpanded:
      return {
        ...currentState,
        focusedListItemUid: null,
        userState: {
          ...currentUserState,
          rooms: currentUserState.rooms.map(room => room.uid === action.roomUid ? {
            ...room,
            expanded: action.expanded,
          } : room),
        }
      }
    case AppStateActions.RenameListTutorialDismissed:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          renameListTutorialDismissed: true,
        }
      }
    case AppStateActions.TypeSelectorTutorialDismissed:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          typeSelectorTutorialDismissed: true,
        }
      }
    case AppStateActions.CustomiseHintShown:
      return {
        ...currentState,
        userState: {
          ...currentUserState,
          customiseHintShownCount: currentUserState.customiseHintShownCount + 1,
        }
      }
    case AppStateActions.CashSalesVisibilityChanged:
      return {
        ...currentState,
        showCashSales: action.visible,
      }
    case AppStateActions.WiserModeChanged:
      return {
        ...currentState,
        optionalWiserMode: action.newMode
      }
    case AppStateActions.ProductSeriesChanged:
      return {
        ...currentState,
        userState: {
          ...currentState.userState,
          selectedProductSeries: action.series,
          userType: action.series === ProductSeries.Pro ? UserType.Electrician : currentState.userState.userType,
        }
      }

    case AppStateActions.DisableWiserHubPrompt:
      return {
        ...currentState,
        hasOptedOutOfWiserHubPrompt: true
      }

    case AppStateActions.DisableMomentarySwitchesPrompt:
      return {
        ...currentState,
        hasOptedOutOfMomentarySwitchesPrompt: true
      }
  }
}

// set flag that list has been shown once the first product has been added
function postUpdateListShownReducer(state: Readonly<AppState>): AppState {
  if (
    !state.userState.hasListBeenShown &&
    (state.userState.configuredProducts.length > 0 || state.userState.rooms.length > 0)
  ) {
    logDebug('AppStateReducer:AppStatePostUpdateReducer', 'setting hasListBeenShown=true')

    return {
      ...state,
      userState: {
        ...state.userState,
        hasListBeenShown: true,
      },
    }
  }

  return state
}

// disable electrician features when not an electrician
function postUpdateElectricianSettingsReducer(state: Readonly<AppState>): AppState {
  if (
    state.userState.userType !== UserType.Electrician && state.userState.moreInfo
  ) {
    return {
      ...state,
      userState: {
        ...state.userState,
        moreInfo: false,
      },
    }
  }

  return state
}

// disable bom menu screen when not an electrician
function postUpdateElectricianBomListSettingsReducer(state: Readonly<AppState>): AppState {
  if (
    state.userState.userType !== UserType.Electrician && state.screen === 'sharingBom'
  ) {
    return {
      ...state,
      screen: null
    }
  }

  return state
}

const postUpdateReducers: ((state: Readonly<AppState>) => AppState)[] = [
  postUpdateListShownReducer,
  postUpdateElectricianSettingsReducer,
  postUpdateElectricianBomListSettingsReducer,
]

function wrapReducerWithDebugLogging(innerReducer: AppStateReducerType): AppStateReducerType {
  if (process.env.NODE_ENV === 'development') {
    return (previousState, action): Readonly<AppState> => {
      const nextState = innerReducer(previousState, action)

      const diff: { -readonly [k in keyof AppState]?: AppState[k] } = {}

      ;(Object.keys(previousState) as (keyof AppState)[]).forEach((key: keyof AppState) => {
        if (previousState[key] !== nextState[key]) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (diff as any)[key] = nextState[key]
        }
      })

      logDebug('AppStateReducer', '\nAction:', action, '\nState Before:', previousState, '\nState After:', nextState, 'diff:', diff)

      return nextState
    }
  }

  return innerReducer

}

const reducerChain: readonly AppStateReducerType[] = [
  wrapReducerWithDebugLogging(
    AppStateActionHandlerReducer,
  ),
  ...postUpdateReducers,
]

export const AppStateReducer: AppStateReducerType = (currentState, action): Readonly<AppState> => {
  return reducerChain.reduce<Readonly<AppState>>((state, currentReducer) => currentReducer(state, action), currentState)
}
